/**
 * MigrationWizardModal Component
 *
 * 4-step wizard for migrating tenant isolation levels:
 * 1. Select Target — choose target isolation level
 * 2. Validate — auto-run validation, show results
 * 3. Confirm — summary with "Start Migration" button
 * 4. Progress — real-time progress bar with step tracking
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircleIcon,
  AlertIcon,
  RefreshIcon,
  CloseIcon,
} from '../../../../components/common/Icons';
import { useMigration } from '../../hooks/useMigration';
import type { Tenant } from '../../types';

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

interface MigrationWizardModalProps {
  isOpen: boolean;
  tenant: Tenant | null;
  onClose: () => void;
  onComplete: () => void;
  authFetch: AuthFetch;
}

const ISOLATION_LABELS: Record<string, string> = {
  row: 'Row-Level Security',
  schema: 'Schema-Based',
  container: 'Container-Based',
};

const ISOLATION_DESCRIPTIONS: Record<string, string> = {
  row: 'Shared database with row-level security policies',
  schema: 'Separate PostgreSQL schema per tenant',
  container: 'Fully isolated Docker containers per tenant',
};

/** Valid migration paths: can only move to a higher isolation level */
function getValidTargets(current: string): string[] {
  switch (current) {
    case 'row': return ['schema', 'container'];
    case 'schema': return ['container'];
    default: return [];
  }
}

export const MigrationWizardModal: React.FC<MigrationWizardModalProps> = ({
  isOpen,
  tenant,
  onClose,
  onComplete,
  authFetch,
}) => {
  const [step, setStep] = useState(1);
  const [target, setTarget] = useState('');
  const migration = useMigration(authFetch);

  // Reset state when modal opens/closes or tenant changes
  useEffect(() => {
    if (isOpen && tenant) {
      setStep(1);
      setTarget('');
      migration.reset();
    }
  }, [isOpen, tenant?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const validTargets = tenant ? getValidTargets(tenant.isolation_level) : [];

  // Auto-validate when entering step 2
  const goToValidation = useCallback(async () => {
    if (!tenant || !target) return;
    setStep(2);
    await migration.validate(tenant.id, target);
  }, [tenant, target, migration]);

  // Start migration from step 3
  const handleStartMigration = useCallback(async () => {
    if (!tenant || !target) return;
    setStep(4);
    await migration.start(tenant.id, target);
  }, [tenant, target, migration]);

  const handleClose = () => {
    migration.reset();
    onClose();
  };

  const handleDone = () => {
    migration.reset();
    onComplete();
  };

  if (!isOpen || !tenant) return null;

  const isTerminal = migration.progress &&
    ['completed', 'failed'].includes(migration.progress.status);

  // Elapsed time calculation
  const elapsed = migration.progress?.started_at
    ? Math.floor((Date.now() - new Date(migration.progress.started_at).getTime()) / 1000)
    : 0;
  const elapsedMin = Math.floor(elapsed / 60);
  const elapsedSec = elapsed % 60;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div
        className="modal modal-lg"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '560px', width: '95%' }}
      >
        {/* Header */}
        <div className="modal-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px 24px 16px',
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px' }}>Migrate Isolation Level</h2>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
              {tenant.name} ({tenant.slug})
            </p>
          </div>
          {(step < 4 || isTerminal) && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={handleClose}
              style={{ padding: '4px' }}
            >
              <CloseIcon size={18} />
            </button>
          )}
        </div>

        {/* Step Indicators */}
        <div style={{
          display: 'flex',
          gap: '4px',
          padding: '0 24px 16px',
        }}>
          {['Select', 'Validate', 'Confirm', 'Progress'].map((label, idx) => (
            <div key={label} style={{
              flex: 1,
              textAlign: 'center',
            }}>
              <div style={{
                height: '3px',
                borderRadius: '2px',
                background: idx + 1 <= step ? 'var(--primary)' : 'rgba(255,255,255,0.1)',
                marginBottom: '6px',
                transition: 'background 0.3s ease',
              }} />
              <span style={{
                fontSize: '11px',
                color: idx + 1 <= step ? 'var(--text-primary)' : 'var(--text-secondary)',
                fontWeight: idx + 1 === step ? 600 : 400,
              }}>
                {label}
              </span>
            </div>
          ))}
        </div>

        {/* Content */}
        <div style={{ padding: '0 24px 24px', minHeight: '200px' }}>

          {/* Step 1: Select Target */}
          {step === 1 && (
            <div>
              <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
                Current isolation: <strong style={{ color: 'var(--text-primary)' }}>
                  {ISOLATION_LABELS[tenant.isolation_level] || tenant.isolation_level}
                </strong>
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {(['schema', 'container'] as const).map((level) => {
                  const isValid = validTargets.includes(level);
                  const isSelected = target === level;

                  return (
                    <label
                      key={level}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        padding: '14px 16px',
                        borderRadius: '10px',
                        border: isSelected
                          ? '1px solid var(--primary)'
                          : '1px solid var(--border-secondary)',
                        background: isSelected
                          ? 'rgba(124, 58, 237, 0.08)'
                          : 'rgba(255,255,255,0.02)',
                        cursor: isValid ? 'pointer' : 'not-allowed',
                        opacity: isValid ? 1 : 0.4,
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <input
                        type="radio"
                        name="migration-target"
                        value={level}
                        checked={isSelected}
                        disabled={!isValid}
                        onChange={() => setTarget(level)}
                        style={{ marginTop: '2px', accentColor: 'var(--primary)' }}
                      />
                      <div>
                        <div style={{ fontWeight: 500, fontSize: '14px', marginBottom: '2px' }}>
                          {ISOLATION_LABELS[level]}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                          {ISOLATION_DESCRIPTIONS[level]}
                        </div>
                        {!isValid && level === tenant.isolation_level && (
                          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                            Current level
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button
                  className="btn btn-primary"
                  disabled={!target}
                  onClick={goToValidation}
                >
                  Next: Validate
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Validate */}
          {step === 2 && (
            <div>
              <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
                Validating migration from{' '}
                <strong style={{ color: 'var(--text-primary)' }}>
                  {ISOLATION_LABELS[tenant.isolation_level]}
                </strong>
                {' '}to{' '}
                <strong style={{ color: 'var(--text-primary)' }}>
                  {ISOLATION_LABELS[target]}
                </strong>
              </p>

              {migration.loading && !migration.validation && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '20px',
                  justifyContent: 'center',
                  color: 'var(--text-secondary)',
                }}>
                  <RefreshIcon size={18} className="spin" />
                  Running validation checks...
                </div>
              )}

              {migration.error && (
                <div style={{
                  padding: '12px 16px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '8px',
                  color: 'var(--error)',
                  fontSize: '13px',
                  marginBottom: '12px',
                }}>
                  <AlertIcon size={14} /> {migration.error}
                </div>
              )}

              {migration.validation && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {/* Status */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    background: migration.validation.valid
                      ? 'rgba(16, 185, 129, 0.1)'
                      : 'rgba(239, 68, 68, 0.1)',
                    border: migration.validation.valid
                      ? '1px solid rgba(16, 185, 129, 0.3)'
                      : '1px solid rgba(239, 68, 68, 0.3)',
                  }}>
                    {migration.validation.valid
                      ? <CheckCircleIcon size={16} />
                      : <AlertIcon size={16} />
                    }
                    <span style={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: migration.validation.valid ? 'var(--success)' : 'var(--error)',
                    }}>
                      {migration.validation.valid ? 'Validation passed' : 'Validation failed'}
                    </span>
                  </div>

                  {/* Data estimate */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '10px',
                  }}>
                    <div style={{
                      padding: '10px 14px',
                      background: 'rgba(255,255,255,0.03)',
                      borderRadius: '8px',
                      border: '1px solid var(--border-secondary)',
                    }}>
                      <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                        Estimated Data Size
                      </div>
                      <div style={{ fontSize: '16px', fontWeight: 600 }}>
                        {migration.validation.estimated_data_size_mb.toFixed(1)} MB
                      </div>
                    </div>
                    <div style={{
                      padding: '10px 14px',
                      background: 'rgba(255,255,255,0.03)',
                      borderRadius: '8px',
                      border: '1px solid var(--border-secondary)',
                    }}>
                      <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                        Estimated Rows
                      </div>
                      <div style={{ fontSize: '16px', fontWeight: 600 }}>
                        {migration.validation.estimated_row_count.toLocaleString()}
                      </div>
                    </div>
                  </div>

                  {/* Errors */}
                  {migration.validation.errors.length > 0 && (
                    <div style={{
                      padding: '10px 14px',
                      background: 'rgba(239, 68, 68, 0.08)',
                      border: '1px solid rgba(239, 68, 68, 0.2)',
                      borderRadius: '8px',
                    }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--error)', marginBottom: '6px' }}>
                        Errors
                      </div>
                      {migration.validation.errors.map((err, i) => (
                        <div key={i} style={{ fontSize: '13px', color: 'var(--error)', padding: '2px 0' }}>
                          {err}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Warnings */}
                  {migration.validation.warnings.length > 0 && (
                    <div style={{
                      padding: '10px 14px',
                      background: 'rgba(245, 158, 11, 0.08)',
                      border: '1px solid rgba(245, 158, 11, 0.2)',
                      borderRadius: '8px',
                    }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--warning)', marginBottom: '6px' }}>
                        Warnings
                      </div>
                      {migration.validation.warnings.map((warn, i) => (
                        <div key={i} style={{ fontSize: '13px', color: 'var(--warning)', padding: '2px 0' }}>
                          {warn}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
                <button className="btn btn-secondary" onClick={() => { setStep(1); migration.reset(); }}>
                  Back
                </button>
                <button
                  className="btn btn-primary"
                  disabled={!migration.validation?.valid || migration.loading}
                  onClick={() => setStep(3)}
                >
                  Next: Confirm
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Confirm */}
          {step === 3 && migration.validation && (
            <div>
              <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
                Review the migration details below before proceeding.
              </p>

              <div style={{
                padding: '16px',
                background: 'rgba(255,255,255,0.03)',
                borderRadius: '10px',
                border: '1px solid var(--border-secondary)',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Tenant</span>
                  <span style={{ fontSize: '13px', fontWeight: 500 }}>{tenant.name}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Migration Path</span>
                  <span style={{ fontSize: '13px', fontWeight: 500 }}>
                    {ISOLATION_LABELS[tenant.isolation_level]}
                    <span style={{ color: 'var(--primary)', margin: '0 8px' }}>&rarr;</span>
                    {ISOLATION_LABELS[target]}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Data Size</span>
                  <span style={{ fontSize: '13px', fontWeight: 500 }}>
                    {migration.validation.estimated_data_size_mb.toFixed(1)} MB
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Row Count</span>
                  <span style={{ fontSize: '13px', fontWeight: 500 }}>
                    {migration.validation.estimated_row_count.toLocaleString()}
                  </span>
                </div>
              </div>

              {migration.validation.warnings.length > 0 && (
                <div style={{
                  marginTop: '12px',
                  padding: '10px 14px',
                  background: 'rgba(245, 158, 11, 0.08)',
                  border: '1px solid rgba(245, 158, 11, 0.2)',
                  borderRadius: '8px',
                  fontSize: '12px',
                  color: 'var(--warning)',
                }}>
                  <strong>Warnings:</strong>
                  {migration.validation.warnings.map((w, i) => (
                    <div key={i} style={{ marginTop: '4px' }}>{w}</div>
                  ))}
                </div>
              )}

              {migration.error && (
                <div style={{
                  marginTop: '12px',
                  padding: '10px 14px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '8px',
                  color: 'var(--error)',
                  fontSize: '13px',
                }}>
                  <AlertIcon size={14} /> {migration.error}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
                <button className="btn btn-secondary" onClick={() => setStep(2)}>
                  Back
                </button>
                <button
                  className="btn btn-primary"
                  disabled={migration.loading}
                  onClick={handleStartMigration}
                >
                  {migration.loading ? (
                    <><RefreshIcon size={14} className="spin" /> Starting...</>
                  ) : (
                    'Start Migration'
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Progress */}
          {step === 4 && (
            <div>
              {/* Progress bar */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '8px',
                }}>
                  <span style={{ fontSize: '13px', fontWeight: 500 }}>
                    {migration.progress?.status === 'completed'
                      ? 'Migration Complete'
                      : migration.progress?.status === 'failed'
                        ? 'Migration Failed'
                        : 'Migrating...'}
                  </span>
                  <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                    {migration.progress ? Math.round(migration.progress.progress_pct) : 0}%
                  </span>
                </div>
                <div style={{
                  height: '8px',
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '4px',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    height: '100%',
                    width: `${migration.progress?.progress_pct || 0}%`,
                    background: migration.progress?.status === 'failed'
                      ? 'var(--error)'
                      : migration.progress?.status === 'completed'
                        ? 'var(--success)'
                        : 'var(--primary)',
                    transition: 'width 0.5s ease',
                    borderRadius: '4px',
                  }} />
                </div>
              </div>

              {/* Status message */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 14px',
                background: 'rgba(255,255,255,0.03)',
                borderRadius: '8px',
                border: '1px solid var(--border-secondary)',
                marginBottom: '12px',
              }}>
                {migration.progress?.status === 'completed' ? (
                  <CheckCircleIcon size={16} />
                ) : migration.progress?.status === 'failed' ? (
                  <AlertIcon size={16} />
                ) : (
                  <RefreshIcon size={16} className="spin" />
                )}
                <span style={{ fontSize: '13px' }}>
                  {migration.progress?.message || 'Starting migration...'}
                </span>
              </div>

              {/* Stats grid */}
              {migration.progress && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '10px',
                  marginBottom: '12px',
                }}>
                  <div style={{
                    padding: '10px 14px',
                    background: 'rgba(255,255,255,0.03)',
                    borderRadius: '8px',
                    border: '1px solid var(--border-secondary)',
                  }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '2px' }}>Steps</div>
                    <div style={{ fontSize: '14px', fontWeight: 600 }}>
                      {migration.progress.current_step} / {migration.progress.total_steps}
                    </div>
                  </div>
                  <div style={{
                    padding: '10px 14px',
                    background: 'rgba(255,255,255,0.03)',
                    borderRadius: '8px',
                    border: '1px solid var(--border-secondary)',
                  }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '2px' }}>Elapsed</div>
                    <div style={{ fontSize: '14px', fontWeight: 600 }}>
                      {elapsedMin}m {elapsedSec.toString().padStart(2, '0')}s
                    </div>
                  </div>
                  <div style={{
                    padding: '10px 14px',
                    background: 'rgba(255,255,255,0.03)',
                    borderRadius: '8px',
                    border: '1px solid var(--border-secondary)',
                  }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '2px' }}>Rows Migrated</div>
                    <div style={{ fontSize: '14px', fontWeight: 600 }}>
                      {migration.progress.rows_migrated.toLocaleString()}
                    </div>
                  </div>
                  <div style={{
                    padding: '10px 14px',
                    background: 'rgba(255,255,255,0.03)',
                    borderRadius: '8px',
                    border: '1px solid var(--border-secondary)',
                  }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '2px' }}>Tables Migrated</div>
                    <div style={{ fontSize: '14px', fontWeight: 600 }}>
                      {migration.progress.tables_migrated}
                    </div>
                  </div>
                </div>
              )}

              {/* Error display */}
              {migration.progress?.status === 'failed' && migration.progress.error && (
                <div style={{
                  padding: '12px 16px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '8px',
                  color: 'var(--error)',
                  fontSize: '13px',
                  marginBottom: '12px',
                }}>
                  <AlertIcon size={14} /> {migration.progress.error}
                </div>
              )}

              {migration.error && !migration.progress?.error && (
                <div style={{
                  padding: '12px 16px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '8px',
                  color: 'var(--error)',
                  fontSize: '13px',
                  marginBottom: '12px',
                }}>
                  <AlertIcon size={14} /> {migration.error}
                </div>
              )}

              {/* Action button */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
                {migration.progress?.status === 'completed' ? (
                  <button className="btn btn-primary" onClick={handleDone}>
                    Done
                  </button>
                ) : migration.progress?.status === 'failed' ? (
                  <button className="btn btn-secondary" onClick={handleClose}>
                    Close
                  </button>
                ) : (
                  <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                    Migration in progress... Do not close this window.
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
